import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { InteractiveContentCardV2 } from '@amzn/seller-university-ui-components';
import { useBundle } from '@amzn/react-arb-tools';
import { t } from 'src/utils/translation';
import { useGetCustomerCredentialQuery } from 'src/redux/slices/api/su';
import { useClientId, useSelLocale } from 'src/contexts/ClientContext';
import { getAppRoute } from 'src/utils/routing';
import { ROUTE_SEARCH_PARAM, ROUTES } from 'src/constants/route';
import { BUNDLE_NAME, CREDENTIAL_STRING_ID } from 'src/constants/localization';
import useUserMetrics from 'src/metrics/useUserMetrics';
import { FeatureName } from 'src/metrics/metricsFields';
import { ContentProgressStatus } from 'src/types/contentProgress';
import { LearningModuleApiData } from 'src/types/api/learningModule';
import { LearningPathwayStaticApiData } from 'src/types/api/learningPathwayStatic';
import { CustomerCredential } from 'src/types/api/credential';

type LearningPathwaySection = LearningPathwayStaticApiData['sections'][number];

/**
 * Bounded set of credential-card states, matching the status pill's distinct visual states 1:1
 * (complete / in-progress / todo).
 */
export type CredentialCardState = 'complete' | 'in-progress' | 'todo';

/**
 * Single source of truth for the credential card's state, shared by the status pill and the
 * impression metric so the emitted `state` can never drift from the pill.
 * Precedence: award => complete, else any started/completed module or in-flight attempt =>
 * in-progress, else todo.
 */
export const deriveCredentialCardState = (
  customerCredential: CustomerCredential,
  learningPathway: LearningPathwayStaticApiData,
): CredentialCardState => {
  if (customerCredential.award) {
    return 'complete';
  }
  // Treat the credential as in-progress when the seller has started/completed any module, not
  // only when the assessment attempt is in progress.
  const anyModuleTouched = (learningPathway.sections ?? []).some(
    (s: LearningPathwaySection) => (s.moduleList ?? ([] as LearningModuleApiData[])).some(
      (m: LearningModuleApiData) => m.moduleProgressStatus === ContentProgressStatus.IN_PROGRESS
        || m.moduleProgressStatus === ContentProgressStatus.COMPLETED,
    ),
  );
  if (anyModuleTouched || customerCredential.assessment?.state === 'IN_PROGRESS') {
    return 'in-progress';
  }
  return 'todo';
};

export interface CredentialCourseCardProps {
  /** Credential learning pathway id the card links to (pilot: amazon-pricing-credential). */
  credentialId: string;
}

/**
 * Credential ingress card prepended to the "Take a self-guided course" section as the
 * carousel's leading card. Sources its title and description from the credential
 * (GetCustomerCredential), reflects the seller's progress in the status pill, and navigates
 * to the credential page. The homepage gates rendering on the credentials pilot weblab.
 */
const CredentialCourseCard: React.FC<CredentialCourseCardProps> = ({ credentialId }) => {
  const navigate = useNavigate();
  const clientId = useClientId();
  const selLocale = useSelLocale();
  const [credentialBundle] = useBundle(BUNDLE_NAME.CREDENTIAL);
  const { clickMetric, viewMetric } = useUserMetrics();

  const { data, isLoading, isError } = useGetCustomerCredentialQuery({
    credentialId,
    clientId,
    selLocale,
  });

  // Fire the card-impression metric once per credentialId, on the first render with data (same
  // gate as the null early-returns below). Keyed on credentialId so a reused instance re-emits
  // for a different credential. Declared before the returns so it is never conditionally executed.
  const lastEmittedImpression = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (lastEmittedImpression.current === credentialId) return;
    if (isLoading || isError || !data) return;
    const { customerCredential } = data;
    const learningPathway = customerCredential?.credential?.learningPathway;
    if (!customerCredential || !learningPathway) return;

    // Derived from the same helper as the status pill, so the emitted state can never drift from it.
    const state = deriveCredentialCardState(customerCredential, learningPathway);

    lastEmittedImpression.current = credentialId;
    viewMetric({
      feature: FeatureName.CredentialCardImpression,
      metadata: { credentialId, state },
    });
  }, [data, isLoading, isError, viewMetric, credentialId]);

  if (isLoading || isError || !data) {
    return null;
  }

  const credential = data.customerCredential?.credential;
  const learningPathway = credential?.learningPathway;
  if (!learningPathway) {
    return null;
  }

  const { customerCredential } = data;
  const buildStatusPill = () => {
    // Same shared derivation as the impression metric, so the pill and metric can never disagree.
    const state = deriveCredentialCardState(customerCredential, learningPathway);
    if (state === 'complete') {
      return { status: 'complete' as const, label: t(credentialBundle, CREDENTIAL_STRING_ID.STATUS_COMPLETED) };
    }
    if (state === 'in-progress') {
      return { status: 'in-progress' as const, label: t(credentialBundle, CREDENTIAL_STRING_ID.STATUS_IN_PROGRESS) };
    }
    return { status: 'todo' as const, label: t(credentialBundle, CREDENTIAL_STRING_ID.STATUS_NOT_STARTED) };
  };

  // Metadata row ("X modules | X assessments | X min"). Each item is entirely derived from the
  // wire and omitted when its count is zero, so the separated row only shows the parts that apply.
  const totalModules = learningPathway.learningPathwayStats?.totalModules ?? 0;
  // A count (not a hardcoded 1) so the copy still reads correctly if assessments ever become a list.
  const assessmentCount = credential?.assessment ? 1 : 0;
  // totalDuration is SECONDS on the wire; convert to whole minutes using the same rounding the
  // learning-pathway banners use so the duration reads consistently across the site.
  const totalDurationSeconds = learningPathway.learningPathwayStats?.totalDuration;
  const durationMinutes = (totalDurationSeconds && Number.isFinite(totalDurationSeconds) && totalDurationSeconds > 0)
    ? Math.round(totalDurationSeconds / 60)
    : 0;
  const metadata = [
    totalModules > 0
      ? t(credentialBundle, CREDENTIAL_STRING_ID.MODULES_COUNT, { count: totalModules })
      : undefined,
    assessmentCount > 0
      ? t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENTS_COUNT, { count: assessmentCount })
      : undefined,
    durationMinutes > 0
      ? t(credentialBundle, CREDENTIAL_STRING_ID.DURATION_MINUTES_SHORT, { minutes: durationMinutes })
      : undefined,
  ].filter((item): item is string => Boolean(item));

  return (
    <InteractiveContentCardV2
      layout="vertical"
      imageSrc={learningPathway.thumbnail || learningPathway.backgroundImage || ''}
      contentVariant="Pdf"
      title={t(credentialBundle, credential?.title ?? '')}
      previewDescription={t(credentialBundle, credential?.description ?? '')}
      metadata={metadata}
      // Right-align the metadata stats on the title row (beside the status pill) to match the
      // approved credential-card design instead of stacking them below the title.
      metadataPlacement="titleRow"
      statusPill={buildStatusPill()}
      ribbon={t(credentialBundle, CREDENTIAL_STRING_ID.HOME_COURSE_CERTIFICATE_RIBBON)}
      onClick={() => {
        clickMetric({
          feature: FeatureName.CredentialCardIngress,
          metadata: { url: window.location.href, credentialId },
        });
        navigate(getAppRoute(`${ROUTES.NEW_LEARN.CREDENTIAL}/${credentialId}?${ROUTE_SEARCH_PARAM.SOURCE}=card`));
      }}
      className="h-full"
    />
  );
};

export default CredentialCourseCard;
